/**
 * Page détail d'un arrêt CJUE
 * Route : /jurisprudence/cjue/:celex
 *
 * Design calqué sur Fiche.tsx :
 *  - Layout 2 colonnes (main + sidebar sticky)
 *  - Card dark "Problème juridique" + Card claire "Solution"
 *  - Section "Fondements" (traités, matières, mots-clés)
 *  - Onglets "Contexte / Arrêts cités / Résumé & Conclusions"
 *  - CTA dark en bas pour les non-connectés
 *  - Sidebar : Impact FR, EUR-Lex, Composition
 */

import { useEffect, useState } from 'react';
import { useParams, Link }     from 'react-router-dom';
import { Helmet }              from 'react-helmet-async';
import { useTranslation }      from 'react-i18next';
import { getDateLocale }       from '@/i18n';
import { Loader }              from '@/components/Loader';
import { PublicHeader, MCPBadge } from '@/components/jurisprudence';
import { FicheAgentChat }      from '@/components/jurisprudence/FicheAgentChat';
import { usePublicAuth }       from '@/hooks/usePublicAuth';

const SITE_URL = 'https://smartlawyer.ai';

// Clés i18n (cjue.fiche.directory.*)
const DIRECTORY_LABELS: Record<string, string> = Object.fromEntries(
  ['PIIC', 'CONC', 'FISC', 'LCPE', 'AGRI', 'ELSJ', 'ENVI', 'PSOC', 'CONS', 'TRAN', 'RAPL', 'DRIN', 'EMPL', 'MARK']
    .map(code => [code, `cjue.fiche.directory.${code}`]),
);

// Convertit un CELEX en référence lisible : 62012CJ0131 → "C-131/12"
// `court` et `suffix` sont des clés i18n (cjue.fiche.court.* / cjue.celex_suffix.*)
function celexToRef(celex: string): { ref: string; court: string; suffix: string } {
  // Accepte 3 à 5 chiffres pour le numéro (ex: 61997TO0275 = T-275/97 ord.)
  const m = celex.match(/^6(\d{4})(CJ|C|T|F)(O?)(\d{3,5})/i);
  if (!m) return { ref: celex, court: 'cjue.fiche.court.CJUE', suffix: '' };
  const [, year, courtRaw, isOrd, numStr] = m;
  const num = parseInt(numStr, 10);
  const courtCode = courtRaw.toUpperCase() === 'CJ' ? 'C' : courtRaw.toUpperCase();
  const courtName: Record<string, string> = {
    C: 'cjue.fiche.court.C', T: 'cjue.fiche.court.T', F: 'cjue.fiche.court.F',
  };
  const yr = year.slice(2);
  const suffix = isOrd ? 'cjue.celex_suffix.ord' : '';
  return {
    ref:   `${courtCode}-${num}/${yr}`,
    court: courtName[courtCode] ?? 'cjue.fiche.court.CJUE',
    suffix,
  };
}

// ── Drapeaux ────────────────────────────────────────────────────────────────
const COUNTRY_FLAG: Record<string, string> = {
  'Germany': '🇩🇪', 'Italy': '🇮🇹', 'France': '🇫🇷', 'Netherlands': '🇳🇱',
  'Belgium': '🇧🇪', 'Spain': '🇪🇸', 'Austria': '🇦🇹',
  'Greece': '🇬🇷', 'Poland': '🇵🇱', 'Portugal': '🇵🇹', 'Ireland': '🇮🇪',
  'Luxembourg': '🇱🇺', 'Denmark': '🇩🇰', 'Romania': '🇷🇴', 'Hungary': '🇭🇺',
  'Czechia': '🇨🇿', 'Czech Republic': '🇨🇿',
  'Bulgaria': '🇧🇬', 'Sweden': '🇸🇪', 'Finland': '🇫🇮',
  'Slovenia': '🇸🇮', 'Latvia': '🇱🇻', 'Lithuania': '🇱🇹', 'Slovakia': '🇸🇰',
  'Estonia': '🇪🇪', 'Cyprus': '🇨🇾', 'Malta': '🇲🇹', 'Croatia': '🇭🇷',
  'The Netherlands': '🇳🇱', 'Holland': '🇳🇱',
  'United Kingdom': '🇬🇧', 'United Kingdom of Great Britain and Northern Ireland': '🇬🇧',
  'United States': '🇺🇸', 'Switzerland': '🇨🇭', 'Norway': '🇳🇴',
  'Iceland': '🇮🇸', 'Liechtenstein': '🇱🇮', 'Turkey': '🇹🇷', 'Türkiye': '🇹🇷',
  'Japan': '🇯🇵', 'Canada': '🇨🇦', 'South Korea': '🇰🇷', 'China': '🇨🇳',
};

// Fallback : langue de procédure → drapeau (pour recours directs sans origin_country)
const LANGUAGE_FLAG: Record<string, string> = {
  'German': '🇩🇪', 'French': '🇫🇷', 'Italian': '🇮🇹', 'Spanish': '🇪🇸',
  'Dutch': '🇳🇱', 'Polish': '🇵🇱', 'Portuguese': '🇵🇹', 'Greek': '🇬🇷',
  'Czech': '🇨🇿', 'Romanian': '🇷🇴', 'Hungarian': '🇭🇺', 'Bulgarian': '🇧🇬',
  'Swedish': '🇸🇪', 'Finnish': '🇫🇮', 'Danish': '🇩🇰', 'Slovak': '🇸🇰',
  'Slovenian': '🇸🇮', 'Lithuanian': '🇱🇹', 'Latvian': '🇱🇻', 'Estonian': '🇪🇪',
  'Croatian': '🇭🇷', 'Maltese': '🇲🇹', 'Irish': '🇮🇪',
  'English': '🇬🇧',
};

/** Retourne les drapeaux à afficher : origin_country en priorité, sinon language_procedure */
function resolveFlags(origin_country?: string[], language_procedure?: string): string[] {
  const fromCountry = (origin_country ?? [])
    .map(c => COUNTRY_FLAG[c])
    .filter(Boolean) as string[];
  if (fromCountry.length > 0) return fromCountry;
  const fromLang = language_procedure ? LANGUAGE_FLAG[language_procedure] : undefined;
  return fromLang ? [fromLang] : [];
}

// Clés i18n (cjue.fiche.suggestions.*) — traduites au rendu
const CJUE_AGENT_SUGGESTIONS = [
  'cjue.fiche.suggestions.simple',
  'cjue.fiche.suggestions.fr_impact',
  'cjue.fiche.suggestions.fr_decisions',
  'cjue.fiche.suggestions.principles',
];

type DeepTab = 'contexte' | 'cites' | 'resume';

// ── Types ─────────────────────────────────────────────────────────────────────
interface CJUEArret {
  id: string; celex: string; ecli?: string;
  date_publication?: string; year_of_resource?: string;
  sector?: string; directory_codes?: string[]; subject_matter?: string[];
  judicial_procedure_type?: string; type_procedure?: string; language_procedure?: string;
  origin_country?: string[];
  resource_legal_type?: string; resource_type?: string;
  source?: string; text_language?: string;
  delivered_by_court_formation?: string;
  advocate_general?: string; judge_rapporteur?: string;
  keywords?: string[]; eurovoc?: string[];
  summary?: string; probleme?: string; solution_text?: string;
  faits?: string; conclusions?: string;
  national_judgement?: string; citing?: string[];
  based_on_treaty?: string[];
  cited_by_count?: number; eurlex_url?: string;
}

// ── Helpers UI ────────────────────────────────────────────────────────────────
function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-5 flex items-center gap-2">
      <span className="h-px flex-1 bg-border" />
      {children}
      <span className="h-px flex-1 bg-border" />
    </h2>
  );
}

function ExpandableText({ text, limit = 600 }: { text: string; limit?: number }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const isLong = text.length > limit;
  return (
    <div>
      <div className={`relative text-sm leading-relaxed text-foreground whitespace-pre-wrap ${!expanded && isLong ? 'max-h-44 overflow-hidden' : ''}`}
           style={{ fontFamily: "'Fraunces', Georgia, serif" }}>
        {text}
        {!expanded && isLong && (
          <div className="absolute bottom-0 left-0 right-0 h-14 bg-gradient-to-t from-background to-transparent pointer-events-none" />
        )}
      </div>
      {isLong && (
        <button onClick={() => setExpanded(e => !e)}
                className="mt-2 text-xs text-indigo-600 hover:underline">
          {expanded ? `↑ ${t('cjue.fiche.collapse')}` : `↓ ${t('cjue.fiche.read_more')}`}
        </button>
      )}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────
export default function PublicCJUEFiche() {
  const { t } = useTranslation();
  const { celex }        = useParams<{ celex: string }>();
  const { user }         = usePublicAuth();
  const [arret, setArret]       = useState<CJUEArret | null>(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState<string | null>(null);
  const [deepTab, setDeepTab]   = useState<DeepTab>('contexte');
  const [chatOpen, setChatOpen] = useState(false);
  const [initialPrompt, setInitialPrompt] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!celex) return;
    setLoading(true); setError(null);
    fetch(`/api/cjue/${encodeURIComponent(celex)}`)
      .then(r => {
        if (!r.ok) throw new Error(r.status === 404 ? t('cjue.fiche.not_found_error') : `HTTP ${r.status}`);
        return r.json();
      })
      .then(setArret)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [celex]);

  if (loading) return (
    <div className="flex justify-center items-center min-h-screen">
      <Loader className="!size-8" />
    </div>
  );

  if (error || !arret) return (
    <div className="flex flex-col items-center justify-center min-h-screen text-center p-8">
      <p className="text-6xl mb-6">🇪🇺</p>
      <h1 className="text-2xl font-bold mb-2">{t('cjue.fiche.not_found_title')}</h1>
      <p className="text-muted-foreground mb-8">{error || t('cjue.fiche.not_found_desc')}</p>
      <Link to="/jurisprudence/cjue"
            className="px-5 py-2.5 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 transition-colors text-sm font-medium">
        ← {t('cjue.fiche.back_to_corpus')}
      </Link>
    </div>
  );

  const displayDate     = arret.date_publication?.slice(0, 10) || arret.year_of_resource || '';
  const mainCodes       = (arret.directory_codes ?? []).filter(c => !c.includes('.'));
  const subCodes        = (arret.directory_codes ?? []).filter(c => c.includes('.'));
  const citedByCount    = arret.cited_by_count ?? 0;
  const citingCount     = arret.citing?.length ?? 0;
  const isGrandeChambre = (arret.delivered_by_court_formation ?? '').toLowerCase().includes('grande');
  const flags           = resolveFlags(arret.origin_country, arret.language_procedure);
  const { ref: celexRefBase, court: courtKey, suffix: suffixKey } = celexToRef(arret.celex);
  const celexRef        = suffixKey ? `${celexRefBase} ${t(suffixKey)}` : celexRefBase;
  const courtName       = t(courtKey);
  const ficheTitle      = `${celexRef}${displayDate ? ' · ' + displayDate : ''}`;
  const pageTitle       = `${celexRef} — ${courtName}`;
  const pageDesc        = arret.summary?.slice(0, 160) ?? arret.probleme?.slice(0, 160) ?? t('cjue.fiche.default_description');

  function openChat(prompt?: string) {
    setInitialPrompt(prompt);
    setChatOpen(true);
  }

  return (
    <>
      <Helmet>
        <title>{pageTitle} | SmartLawyer</title>
        <meta name="description"  content={pageDesc} />
        <link rel="canonical"     href={`${SITE_URL}/jurisprudence/cjue/${celex}`} />
        <meta property="og:title" content={`${pageTitle} | SmartLawyer`} />
        <meta property="og:url"   content={`${SITE_URL}/jurisprudence/cjue/${celex}`} />
        <meta name="robots"       content="index, follow" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <PublicHeader />

        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
          <div className="flex gap-8 items-start">

            {/* ══════════════════════════════════════════════════════════════
                Colonne principale
            ══════════════════════════════════════════════════════════════ */}
            <main className="flex-1 min-w-0">

              {/* Breadcrumb */}
              <nav className="text-xs text-muted-foreground mb-6 flex items-center gap-1.5 flex-wrap">
                <Link to="/" className="hover:text-foreground transition-colors">{t('cjue.breadcrumb.home')}</Link>
                <span>/</span>
                <Link to="/browse" className="hover:text-foreground transition-colors">{t('nav.jurisprudence')}</Link>
                <span>/</span>
                <Link to="/jurisprudence/cjue" className="hover:text-foreground transition-colors">{t('cjue.abbr')}</Link>
                <span>/</span>
                <span className="text-foreground font-mono">{celex}</span>
              </nav>

              {/* En-tête */}
              <div className="mb-8">
                <div className="flex items-center gap-2 mb-3 flex-wrap">
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold tracking-widest uppercase px-2.5 py-1 rounded-full bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-700">
                    🇪🇺 {t('cjue.abbr')}
                  </span>
                  {flags.length > 0 && (
                    <span className="text-lg leading-none" title={arret.origin_country?.join(', ') ?? arret.language_procedure}>
                      {flags.join(' ')}
                    </span>
                  )}
                  {arret.judicial_procedure_type && (
                    <span className="text-[10px] px-2.5 py-1 rounded-full bg-violet-100 text-violet-700 dark:bg-violet-900/20 dark:text-violet-300 border border-violet-200 font-medium">
                      {arret.judicial_procedure_type}
                    </span>
                  )}
                  {isGrandeChambre && (
                    <span className="text-[10px] px-2.5 py-1 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400 border border-amber-200 font-bold">
                      ⭐ {t('cjue.stats.grand_chamber')}
                    </span>
                  )}
                  {arret.resource_legal_type && (
                    <span className="text-[10px] px-2.5 py-1 rounded-full bg-muted text-muted-foreground border">
                      {arret.resource_legal_type}
                    </span>
                  )}
                </div>

                <h1 className="text-2xl sm:text-3xl font-bold text-foreground leading-tight mb-1"
                    style={{ fontFamily: "'Fraunces', Georgia, serif" }}>
                  {celexRef}
                </h1>
                <p className="text-sm text-muted-foreground flex flex-wrap items-center gap-x-2">
                  <span>{courtName}</span>
                  {arret.delivered_by_court_formation && (
                    <>
                      <span className="text-border">·</span>
                      <span className={isGrandeChambre ? 'font-semibold text-amber-600 dark:text-amber-400' : ''}>
                        {arret.delivered_by_court_formation}
                      </span>
                    </>
                  )}
                  {displayDate && (
                    <>
                      <span className="text-border">·</span>
                      <span>{displayDate}</span>
                    </>
                  )}
                </p>
                <p className="text-xs text-muted-foreground/40 font-mono mt-0.5">
                  {arret.celex}
                  {arret.ecli && <span className="ml-3">{arret.ecli}</span>}
                </p>

                {/* Matières principales */}
                {mainCodes.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {mainCodes.map(c => (
                      <Link key={c} to={`/jurisprudence/cjue?sector=${c}`}
                            className="text-[11px] px-2.5 py-0.5 rounded-full bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-700 hover:bg-indigo-200 transition-colors font-medium">
                        {DIRECTORY_LABELS[c] ? t(DIRECTORY_LABELS[c]) : c}
                      </Link>
                    ))}
                    {subCodes.slice(0, 4).map(c => (
                      <span key={c} className="text-[10px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground border font-mono">
                        {c}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* ── 1. Ce qu'il faut retenir ──────────────────────────────── */}
              <section className="mb-12" id="retenir">
                <SectionTitle>{t('cjue.fiche.key_takeaways')}</SectionTitle>

                {/* Problème — card dark */}
                {arret.probleme && (
                  <div className="rounded-2xl bg-foreground text-background p-6 mb-3 shadow-lg">
                    <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                      <p className="text-[10px] font-bold uppercase tracking-widest opacity-50">{t('cjue.fiche.legal_issue')}</p>
                      {arret.delivered_by_court_formation && (
                        <span className="text-[10px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full bg-white/10 text-white/80">
                          {arret.delivered_by_court_formation}
                        </span>
                      )}
                    </div>
                    <p className="text-base leading-relaxed font-medium"
                       style={{ fontFamily: "'Fraunces', Georgia, serif" }}>
                      {arret.probleme}
                    </p>
                  </div>
                )}

                {/* Solution — card claire */}
                {arret.solution_text ? (
                  <div className="rounded-2xl border-2 border-border bg-card p-6 shadow-sm">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-3">
                      {t('cjue.fiche.court_decision')}
                    </p>
                    <p className="text-sm leading-relaxed text-foreground"
                       style={{ fontFamily: "'Fraunces', Georgia, serif" }}>
                      {arret.solution_text}
                    </p>
                  </div>
                ) : arret.summary ? (
                  <div className="rounded-2xl border-2 border-emerald-200 dark:border-emerald-800 bg-emerald-50/40 dark:bg-emerald-950/10 p-6">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-700 dark:text-emerald-400 mb-3">
                      {t('cjue.fiche.official_summary')}
                    </p>
                    <ExpandableText text={arret.summary} />
                  </div>
                ) : null}
              </section>

              {/* ── 2. Fondements ────────────────────────────────────────────── */}
              {((arret.based_on_treaty?.length ?? 0) > 0 ||
                (arret.keywords?.length ?? 0) > 0 ||
                (arret.eurovoc?.length ?? 0) > 0) && (
                <section className="mb-12" id="fondements">
                  <SectionTitle>{t('cjue.fiche.legal_bases')}</SectionTitle>

                  {(arret.based_on_treaty?.length ?? 0) > 0 && (
                    <div className="mb-4">
                      <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-2">{t('cjue.fiche.reference_texts')}</p>
                      <div className="flex flex-wrap gap-2">
                        {arret.based_on_treaty!.map((tr, i) => (
                          <span key={i}
                                className="inline-flex items-center gap-1.5 text-sm font-mono bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 px-3 py-1.5 rounded-full">
                            {tr}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {(arret.keywords?.length ?? 0) > 0 && (
                    <div className="mb-4">
                      <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-2">{t('cjue.fiche.keywords')}</p>
                      <div className="flex flex-wrap gap-2">
                        {arret.keywords!.map((k, i) => (
                          <span key={i}
                                className="text-sm bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 px-3 py-1.5 rounded-full">
                            {k}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {(arret.eurovoc?.length ?? 0) > 0 && (
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-2">{t('cjue.fiche.eurovoc')}</p>
                      <div className="flex flex-wrap gap-1.5">
                        {arret.eurovoc!.slice(0, 15).map((k, i) => (
                          <span key={i}
                                className="text-xs bg-muted text-muted-foreground px-2.5 py-1 rounded-lg">
                            {k}
                          </span>
                        ))}
                        {(arret.eurovoc?.length ?? 0) > 15 && (
                          <span className="text-xs text-muted-foreground px-2 py-1">+{arret.eurovoc!.length - 15}</span>
                        )}
                      </div>
                    </div>
                  )}
                </section>
              )}

              {/* ── 3. Bannière promo (non connectés) ─────────────────────── */}
              {!user && (
                <div className="mb-12 flex items-center gap-4 rounded-xl border border-primary/20 bg-primary/5 px-5 py-3.5">
                  <span className="text-xl shrink-0">🇪🇺</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-foreground">SmartLawyer Pro</p>
                    <p className="text-xs text-muted-foreground">
                      {t('cjue.fiche.promo_text')}
                    </p>
                  </div>
                  <Link to="/pricing"
                        className="shrink-0 text-xs font-semibold px-3 py-1.5 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors">
                    {t('cjue.fiche.try')}
                  </Link>
                </div>
              )}

              {/* ── 4. Approfondir (onglets) ──────────────────────────────── */}
              <section className="mb-12" id="approfondir">
                <SectionTitle>{t('cjue.fiche.go_deeper')}</SectionTitle>

                <div className="flex gap-1 mb-6 border-b overflow-x-auto">
                  {([
                    ['contexte', `📖 ${t('cjue.fiche.tabs.context')}`],
                    ['cites',    `🔗 ${t('cjue.fiche.tabs.cited')}${citingCount ? ` (${citingCount})` : ''}`],
                    ['resume',   `📜 ${t('cjue.fiche.tabs.summary')}`],
                  ] as [DeepTab, string][]).map(([key, label]) => (
                    <button key={key} onClick={() => setDeepTab(key)}
                            className={`px-4 py-2 text-sm font-medium rounded-t-lg -mb-px border transition-colors whitespace-nowrap ${
                              deepTab === key
                                ? 'bg-background border-border border-b-background text-foreground'
                                : 'border-transparent text-muted-foreground hover:text-foreground'
                            }`}>
                      {label}
                    </button>
                  ))}
                </div>

                {/* Contexte & procédure */}
                {deepTab === 'contexte' && (
                  <div className="space-y-6">
                    {arret.faits && (
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-3">{t('cjue.fiche.facts')}</p>
                        <ExpandableText text={arret.faits} />
                      </div>
                    )}
                    {arret.national_judgement && (
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-2">{t('cjue.fiche.national_judgement')}</p>
                        <p className="text-sm text-foreground bg-muted/40 rounded-xl px-4 py-3 border">{arret.national_judgement}</p>
                      </div>
                    )}
                    {((arret.origin_country?.length ?? 0) > 0 || arret.type_procedure || arret.language_procedure) && (
                      <div className="rounded-xl border bg-card p-4 space-y-2">
                        {(arret.origin_country?.length ?? 0) > 0 && (
                          <div className="flex gap-3 text-sm">
                            <span className="text-muted-foreground min-w-[10rem]">{t('cjue.fiche.member_state')}</span>
                            <span className="font-medium">
                              {arret.origin_country!.map(c => [COUNTRY_FLAG[c], c].filter(Boolean).join(' ')).join(', ')}
                            </span>
                          </div>
                        )}
                        {arret.type_procedure && (
                          <div className="flex gap-3 text-sm">
                            <span className="text-muted-foreground min-w-[10rem]">{t('cjue.fiche.procedure_type')}</span>
                            <span className="font-medium">{arret.type_procedure}</span>
                          </div>
                        )}
                        {arret.language_procedure && (
                          <div className="flex gap-3 text-sm">
                            <span className="text-muted-foreground min-w-[10rem]">{t('cjue.fiche.procedure_language')}</span>
                            <span className="font-medium">
                              {LANGUAGE_FLAG[arret.language_procedure] && (
                                <span className="mr-1">{LANGUAGE_FLAG[arret.language_procedure]}</span>
                              )}
                              {arret.language_procedure}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                    {!arret.faits && !arret.national_judgement && (
                      <p className="text-sm text-muted-foreground text-center py-8">{t('cjue.fiche.no_context')}</p>
                    )}
                  </div>
                )}

                {/* Arrêts cités */}
                {deepTab === 'cites' && (
                  <div>
                    {citingCount > 0 ? (
                      <>
                        <p className="text-sm text-muted-foreground mb-4">
                          {t('cjue.fiche.cites_count', { count: citingCount })}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {arret.citing!.map((c, i) => (
                            <Link key={i}
                                  to={`/jurisprudence/cjue/${encodeURIComponent(c)}`}
                                  className="text-sm font-mono px-3 py-1.5 rounded-full bg-indigo-50 dark:bg-indigo-950/30 text-indigo-600 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-800 hover:bg-indigo-100 transition-colors">
                              {c}
                            </Link>
                          ))}
                        </div>
                      </>
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-8">{t('cjue.fiche.no_cited')}</p>
                    )}
                  </div>
                )}

                {/* Résumé & conclusions */}
                {deepTab === 'resume' && (
                  <div className="space-y-6">
                    {arret.summary && (
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-3">{t('cjue.fiche.official_summary')}</p>
                        <ExpandableText text={arret.summary} limit={800} />
                      </div>
                    )}
                    {arret.conclusions && (
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-3">
                          {t('cjue.fiche.ag_opinion')}{arret.advocate_general ? ` — ${arret.advocate_general}` : ''}
                        </p>
                        <ExpandableText text={arret.conclusions} limit={800} />
                      </div>
                    )}
                    {!arret.summary && !arret.conclusions && (
                      <p className="text-sm text-muted-foreground text-center py-8">{t('cjue.fiche.no_summary')}</p>
                    )}
                  </div>
                )}
              </section>

              {/* ── 5. CTA dark ───────────────────────────────────────────── */}
              {!user && (
                <div className="rounded-2xl bg-foreground text-background p-8 mb-12 text-center shadow-xl">
                  <p className="text-xs font-bold uppercase tracking-widest opacity-40 mb-3">SmartLawyer</p>
                  <h3 className="text-xl font-bold mb-2"
                      style={{ fontFamily: "'Fraunces', Georgia, serif" }}>
                    {t('cjue.fiche.cta_title')}
                  </h3>
                  <p className="text-sm opacity-70 mb-6 max-w-md mx-auto">
                    {t('cjue.fiche.cta_text')}
                  </p>
                  <div className="flex justify-center gap-3 flex-wrap">
                    <Link to="/pricing"
                          className="px-5 py-2.5 rounded-xl bg-background text-foreground font-semibold text-sm hover:bg-background/90 transition-colors">
                      {t('cjue.fiche.see_plans')}
                    </Link>
                    <Link to="/jurisprudence/cjue"
                          className="px-5 py-2.5 rounded-xl border border-white/20 text-background/80 font-medium text-sm hover:border-white/40 transition-colors">
                      {t('cjue.fiche.explore_corpus')}
                    </Link>
                  </div>
                </div>
              )}

              <footer className="mt-12 pt-8 border-t text-xs text-muted-foreground text-center">
                <p>© SmartLawyer · {t('cjue.search.corpus_title')} — {t('cjue.fiche.source_eurlex')}</p>
                <p className="mt-1 flex justify-center gap-4">
                  <Link to="/jurisprudence/cjue"  className="hover:text-foreground transition-colors">{t('cjue.search.corpus_title')}</Link>
                  <Link to="/jurisprudence"        className="hover:text-foreground transition-colors">{t('cjue.fiche.french_case_law')}</Link>
                  <Link to="/mentions-legales"     className="hover:text-foreground transition-colors">{t('cjue.fiche.legal_notice')}</Link>
                </p>
              </footer>
            </main>

            {/* ══════════════════════════════════════════════════════════════
                Sidebar desktop (sticky)
            ══════════════════════════════════════════════════════════════ */}
            <aside className="hidden lg:flex flex-col gap-4 shrink-0 w-64 sticky top-6">

              {/* Impact en droit français */}
              <div className="rounded-2xl border bg-card shadow-sm overflow-hidden">
                <div className="px-4 py-3 bg-gradient-to-br from-amber-50/80 to-amber-50/20 dark:from-amber-950/20 border-b">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-amber-700 dark:text-amber-400 mb-1">
                    {t('cjue.fiche.fr_impact')}
                  </p>
                  <div className="flex items-baseline gap-1.5">
                    <span className={`text-3xl font-bold tabular-nums ${citedByCount >= 20 ? 'text-amber-600 dark:text-amber-400' : 'text-foreground'}`}>
                      {citedByCount.toLocaleString(getDateLocale())}
                    </span>
                    <span className="text-xs text-muted-foreground">{t('cjue.fiche.fr_decisions_label', { count: citedByCount })}</span>
                  </div>
                  {citedByCount === 0 && (
                    <p className="text-xs text-muted-foreground mt-1">{t('cjue.fiche.not_cited_fr')}</p>
                  )}
                </div>
                {citedByCount > 0 && (
                  <div className="px-4 py-3">
                    <Link to={`/jurisprudence?q=${encodeURIComponent(arret.celex)}`}
                          className="w-full flex items-center justify-center gap-1.5 px-4 py-2 rounded-xl bg-amber-100 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 text-xs font-semibold hover:bg-amber-200 dark:hover:bg-amber-900/40 transition-colors border border-amber-200 dark:border-amber-800">
                      {t('cjue.fiche.see_fr_decisions')} →
                    </Link>
                  </div>
                )}
              </div>

              {/* Agent IA CJUE */}
              <div className="rounded-2xl border bg-card shadow-sm overflow-hidden">
                <div className="px-4 py-3 bg-gradient-to-br from-primary/5 to-primary/10 border-b">
                  <div className="flex items-center gap-2.5 mb-1">
                    <span className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-base flex-shrink-0">
                      🇪🇺
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-foreground truncate">Juris Europa</p>
                      <p className="text-[10px] text-muted-foreground">{t('cjue.fiche.agent_subtitle')}</p>
                    </div>
                    <span className="ml-auto text-[9px] font-bold bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 px-1.5 py-0.5 rounded-full shrink-0">{t('cjue.fiche.ai_badge')}</span>
                  </div>
                </div>
                <div className="px-4 py-3 space-y-2">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-2">{t('cjue.fiche.suggested_questions')}</p>
                  {CJUE_AGENT_SUGGESTIONS.map((s, i) => (
                    <button key={i} onClick={() => openChat(t(s))}
                            className="w-full text-left text-xs px-3 py-2 rounded-lg border border-border bg-background hover:bg-muted/50 hover:border-primary/30 transition-colors leading-snug">
                      {t(s)}
                    </button>
                  ))}
                </div>
                <div className="px-4 pb-4">
                  <button onClick={() => openChat()}
                          className="w-full flex items-center justify-center gap-1.5 px-4 py-2 rounded-xl bg-primary/10 text-primary text-xs font-semibold hover:bg-primary/20 transition-colors border border-primary/20">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                            d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                    {t('cjue.fiche.analyze_ai')}
                  </button>
                </div>
              </div>

              {/* EUR-Lex */}
              <div className="rounded-2xl border bg-card shadow-sm overflow-hidden">
                <div className="px-4 py-3 bg-gradient-to-br from-indigo-50/80 to-indigo-50/10 dark:from-indigo-950/20 border-b">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-indigo-600 dark:text-indigo-400 mb-0.5">{t('cjue.fiche.full_text')}</p>
                  <p className="text-xs text-muted-foreground">{t('cjue.fiche.official_source')}</p>
                </div>
                <div className="px-4 py-3">
                  {arret.eurlex_url ? (
                    <a href={arret.eurlex_url} target="_blank" rel="noopener noreferrer"
                       className="w-full flex items-center justify-center gap-1.5 px-4 py-2 rounded-xl bg-indigo-600 text-white text-xs font-semibold hover:bg-indigo-700 transition-colors">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                              d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                      {t('cjue.fiche.open_eurlex')}
                    </a>
                  ) : (
                    <p className="text-xs text-muted-foreground text-center">{t('cjue.fiche.link_unavailable')}</p>
                  )}
                </div>
              </div>

              {/* Composition de la Cour */}
              {(arret.judge_rapporteur || arret.advocate_general || arret.delivered_by_court_formation) && (
                <div className="rounded-2xl border bg-card shadow-sm overflow-hidden">
                  <div className="px-4 py-3 border-b">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{t('cjue.fiche.composition')}</p>
                  </div>
                  <div className="px-4 py-3 space-y-2.5">
                    {arret.delivered_by_court_formation && (
                      <div>
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-0.5">{t('cjue.fiche.formation')}</p>
                        <p className="text-xs font-medium text-foreground">{arret.delivered_by_court_formation}</p>
                      </div>
                    )}
                    {arret.judge_rapporteur && (
                      <div>
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-0.5">{t('cjue.fiche.judge_rapporteur')}</p>
                        <p className="text-xs font-medium text-foreground">{arret.judge_rapporteur}</p>
                      </div>
                    )}
                    {arret.advocate_general && (
                      <div>
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-0.5">{t('cjue.fiche.advocate_general')}</p>
                        <p className="text-xs font-medium text-foreground">{arret.advocate_general}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Autres arrêts — même matière */}
              {mainCodes.length > 0 && (
                <div className="rounded-2xl border bg-card shadow-sm overflow-hidden">
                  <div className="px-4 py-3 border-b">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{t('cjue.fiche.same_subject')}</p>
                  </div>
                  <div className="px-4 py-3 space-y-1.5">
                    {mainCodes.slice(0, 4).map(c => (
                      <Link key={c} to={`/jurisprudence/cjue?sector=${c}`}
                            className="flex items-center justify-between text-xs px-2.5 py-1.5 rounded-lg hover:bg-muted/50 transition-colors group">
                        <span className="text-foreground group-hover:text-indigo-600 transition-colors">
                          {DIRECTORY_LABELS[c] ? t(DIRECTORY_LABELS[c]) : c}
                        </span>
                        <svg className="w-3 h-3 text-muted-foreground group-hover:text-indigo-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/>
                        </svg>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

            </aside>
          </div>
        </div>
      </div>

      {/* Chat overlay */}
      {chatOpen && (
        <>
          <div className="fixed inset-0 z-40 bg-background/60 backdrop-blur-sm"
               onClick={() => setChatOpen(false)} />
          <div className="fixed inset-x-0 bottom-0 z-50 flex flex-col rounded-t-3xl bg-background border-t border-x border-border shadow-2xl"
               style={{ maxHeight: '85vh', animation: 'slideUp 0.3s ease-out' }}>
            {/* Bande contexte */}
            <div className="flex items-center gap-3 px-5 py-3 bg-foreground text-background rounded-t-3xl shrink-0">
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-bold uppercase tracking-widest opacity-50 mb-0.5">{t('cjue.fiche.analyzing')}</p>
                <p className="text-sm font-medium truncate" style={{ fontFamily: "'Fraunces', Georgia, serif" }}>
                  {celexRef} · {courtName}{displayDate ? ' · ' + displayDate : ''}
                </p>
                {arret.probleme && (
                  <p className="text-xs opacity-60 truncate mt-0.5">{arret.probleme.slice(0, 90)}…</p>
                )}
              </div>
              <button onClick={() => setChatOpen(false)} aria-label={t('common.close')}
                      className="shrink-0 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            {/* Suggestions rapides */}
            {!initialPrompt && (
              <div className="flex gap-2 px-5 py-2.5 border-b overflow-x-auto shrink-0">
                {CJUE_AGENT_SUGGESTIONS.map((s, i) => (
                  <button key={i} onClick={() => setInitialPrompt(t(s))}
                          className="shrink-0 text-xs px-3 py-1.5 rounded-full border border-border bg-muted/50 hover:bg-muted hover:border-primary/30 transition-colors whitespace-nowrap">
                    {t(s)}
                  </button>
                ))}
              </div>
            )}
            <div className="flex-1 overflow-hidden">
              <FicheAgentChat
                ficheId={arret.celex}
                ficheTitle={ficheTitle}
                initialPrompt={initialPrompt}
                key={`${arret.celex}-${initialPrompt ?? 'open'}`}
              />
            </div>
          </div>
        </>
      )}

      <style>{`
        @keyframes slideUp {
          from { transform: translateY(100%); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
      `}</style>

      <MCPBadge />
    </>
  );
}
